/**
 * save-flow — end-to-end Save handler.
 *
 * Wires the local SPA op log to the server's per-format edit endpoint:
 *   - PDF:  ``POST /api/editor-edit/<jobId>``
 *   - PPTX: ``POST /api/editor-edit-pptx/<jobId>`` (Phase 3)
 *   - DOCX: ``POST /api/editor-edit-docx/<jobId>`` (Phase 3)
 *
 * On a successful round-trip the SPA re-fetches the IR (so it sees the
 * canonical post-write state) and clears the local oplog so undo doesn't
 * try to re-revert across the save boundary.
 *
 * ## Phase 4 additions (cont-37 cadence)
 *
 * **Auto-save-every-~N-ops:** `AutoSaveState` tracks `opsSinceFlush` and
 * flushes when it reaches `editorAutoSaveOpThreshold` (default 10,
 * hot-configurable via `POST /api/admin/config`). The counter resets ONLY
 * on flush *success* (ESE-7: failed flush retains the tail).
 *
 * **download-triggers-save:** `flushBeforeDownload()` awaits any in-flight
 * flush, then forces a threshold-independent flush of the residual tail.
 *
 * **Concurrency guard (ESE-8):** a single in-flight-flush promise prevents
 * double-POST when an auto-save flush and a download-triggered flush race.
 * The download flush awaits the in-flight promise, then flushes whatever
 * remains.
 *
 * Wire shape: matches ``EditOpEnvelope`` in
 * ``backend/src/AdaTool.PdfModel/Model/EditOp.cs``, with the discriminator
 * vocabulary pinned by
 * ``system-models/wire-contracts/editor-edit-op.schema.json`` (Phase 0
 * of docs/design/wire-contracts-migration-project.md). Most ops
 * pass through 1:1; two SPA-specific kinds map non-trivially:
 *   - ``set-decorative`` is rendered as ``set-alt-text`` with empty alt
 *     text (the SPA-side decorative flag is rendered as ``/Alt = ""`` in
 *     the saved PDF; this is the editor's de-facto convention).
 *   - ``set-title`` and ``set-attestation`` are SPA-only and dropped
 *     from the envelope (no PDF struct-elem maps).
 *
 * Pre-Phase-0 the wire discriminator was ``"set-alt"`` here while JS+WASM
 * elsewhere used ``"set-alt-text"`` — silent drift between the two surfaces
 * of the same wire boundary. Phase 0 renamed C# to match the JS literal,
 * so the SPA-side ``opToWireOp`` translation now passes the kind through
 * 1:1.
 *
 * @module save-flow
 */
import { appendTokenIfPresent } from "./url-token.js";
import { bboxArrayToObject } from "./mcid-reassociate.js";
import { ROLE_UNDEFINED } from "./role-constants.js";
import { log } from "./log.js";
// ─── Auto-save configuration ──────────────────────────────────────────────────
/**
 * Default auto-save op threshold (matches design-doc §5.4 baseline and the
 * `editorAutoSaveOpThreshold` config knob default in `web/api/bootstrap.py`).
 *
 * WHY a module-level constant: the `POST /api/admin/config` knob
 * `editorAutoSaveOpThreshold` controls the server-side value; callers can
 * override the threshold at runtime by calling `setAutoSaveOpThreshold()`.
 * This makes the seam testable without DOM or fetch setup.
 */
const DEFAULT_AUTO_SAVE_OP_THRESHOLD = 10;
let _autoSaveOpThreshold = DEFAULT_AUTO_SAVE_OP_THRESHOLD;
/** Update the op-threshold at runtime (e.g. from a server-injected page value). */
export function setAutoSaveOpThreshold(n) {
    if (typeof n === "number" && Number.isFinite(n) && n > 0) {
        _autoSaveOpThreshold = Math.floor(n);
    }
}
/** Read the current op-threshold (for tests + diagnostics). */
export function getAutoSaveOpThreshold() {
    return _autoSaveOpThreshold;
}
// ─── In-flight flush promise (ESE-8 concurrency guard) ───────────────────────
/**
 * The single in-flight-flush promise.
 *
 * WHY a module-level var: `save-flow` is a module singleton per browser tab.
 * Keeping the promise here — rather than in a class — mirrors the existing
 * stateless-helper style of this module while still giving the ESE-8 guard
 * a stable home. A class would be the right shape if ≥2 mutable vars were
 * shared across exported functions (CLAUDE.md rule #11a), but this is the // noqa: bare-hex — rule reference in JSDoc comment, not a UI color literal
 * FIRST mutable state introduced here; the class would be premature.
 * Extract on the second site if `_autoSaveState` or a third var joins.
 */
let _inFlightFlushPromise = null;
/**
 * Shared `AutoSaveState` — exported for external flush-state reads and tests.
 *
 * Callers must treat this as read-only; mutations go through `recordOpApplied`,
 * `flush`, and `flushBeforeDownload`.
 */
// ─── Client exactly-once floor (FOLD-1, RCA editor-download-drops-edits-260907
// §5.3) ──────────────────────────────────────────────────────────────────────
//
// The server now applies each POSTed op to the CURRENT checkpoint (Phase-2
// apply-to-checkpoint), so re-POSTing an already-applied op double-applies it
// (H4′). Each flush POSTs only the un-applied TAIL: we remember how many oplog
// entries have been successfully flushed (per opLog identity — an explicit save
// swaps in a fresh OpLog, resetting the epoch) and slice the snapshot from
// there. This is the client floor; the server-side (sessionId, opSeq) dedup is
// the belt that holds exactly-once regardless of client correctness.
let _flushedOpLog = null;
let _flushedEntryCount = 0;
/** Reset the flushed-entry high-water (tests + a new editor session). */
export function resetFlushHighWater() {
    _flushedOpLog = null;
    _flushedEntryCount = 0;
}
export const autoSaveState = {
    opsSinceFlush: 0,
    flushedSeq: 0,
    pending: false,
    lastFlushTs: 0,
};
const _autoSaveObservers = new Set();
/** Subscribe to auto-save start/stop transitions. Returns an unsubscribe fn. */
export function onAutoSaveStateChange(fn) {
    _autoSaveObservers.add(fn);
    return () => { _autoSaveObservers.delete(fn); };
}
function _notifyAutoSaveState(pending) {
    for (const fn of _autoSaveObservers) {
        try {
            fn(pending);
        }
        catch (err) {
            // An observer throwing must not corrupt the flush lifecycle.
            log.warn("[save-flow] auto-save observer threw:", err);
        }
    }
}
// ─── Struct-tree root sentinel ────────────────────────────────────────────────
/**
 * Path-form structId of the struct-tree ROOT. A top-level (whole-document)
 * reorder or a doc-level set-lang targets the root; the server resolves this
 * to `StructId.Root`. Named so the `?? STRUCT_TREE_ROOT_ID` fallbacks below are
 * an EXPLICIT "target the document root" intent (Ph1b §6 boyscout, A.14 spirit)
 * rather than a bare magic string.
 */
const STRUCT_TREE_ROOT_ID = "S";
// ─── opToWireOp — the load-bearing discriminated-union dispatch ───────────────
// Return type is EditorEditOp (generated from the wire-contracts schema) so
// discriminator-string drift becomes a compile error. Phase E type-safety win
// per docs/design/typescript-migration-analysis-260521.md §3.1 Bug class B.
export function opToWireOp(op) {
    if (!op || typeof op.kind !== "string")
        return null;
    switch (op.kind) {
        case "set-alt-text":
            // Pass through 1:1 — the wire discriminator IS "set-alt-text"
            // post-Phase-0 (CLUSTER 2 of the wire-contracts migration).
            return {
                kind: "set-alt-text",
                structId: op.structId,
                beforeAlt: (op.before) ?? "",
                afterAlt: (op.after) ?? "",
            };
        case "set-decorative":
            // The SPA's "mark decorative" toggle is rendered as
            // ``/Alt = ""`` in the saved PDF (the artifact-marking
            // convention used by the screen-reader pipeline).
            return {
                kind: "set-alt-text",
                structId: op.structId,
                beforeAlt: op.before ? "" : (op.beforeAlt ?? ""),
                afterAlt: op.after ? "" : (op.afterAlt ?? ""),
            };
        case "set-actual-text":
            return {
                kind: "set-actual-text",
                structId: op.structId,
                beforeActualText: (op.before) ?? "",
                afterActualText: (op.after) ?? "",
            };
        case "set-role":
            return {
                kind: "set-role",
                structId: op.structId,
                beforeRole: (op.before) ?? "",
                afterRole: (op.after) ?? "",
            };
        case "set-lang":
            // SPA-side ``set-lang`` is a doc-level edit (ir.language).
            // The wire op is per-StructId; route it to the doc root if
            // a structId isn't supplied.
            return {
                kind: "set-lang",
                structId: op.structId ?? STRUCT_TREE_ROOT_ID,
                beforeLang: (op.before) ?? "",
                afterLang: (op.after) ?? "",
            };
        case "reorder-reading":
            // parentId discriminates the reorder target (Phase 4a, Ph1b R2):
            //   - an item reorder carries the List block's structId;
            //   - a top-level page reorder carries no parentId → the struct-tree
            //     ROOT ("S"), which the server's ApplyReorder resolves to
            //     StructId.Root. The `?? STRUCT_TREE_ROOT_ID` is the EXPLICIT
            //     top-level-reorder fallback (not a silent catch-all): a missing
            //     parentId means "reorder the document's top-level blocks",
            //     which is exactly a reorder rooted at the struct-tree root.
            return {
                kind: "reorder-reading",
                parentId: op.parentId ?? STRUCT_TREE_ROOT_ID,
                beforeOrder: Array.isArray(op.before) ? op.before : [],
                afterOrder: Array.isArray(op.after) ? op.after : [],
            };
        case "set-bbox":
            // Per `EditOp.SetBBoxOp` in C# — the wire shape carries
            // typed BeforeBBox / AfterBBox objects (not raw arrays) so
            // the server-side helper can route to the
            // /A[/Layout]/BBox attribute write. Translate the SPA's
            // [x, y, w, h] arrays into the typed envelope here.
            return {
                kind: "set-bbox",
                structId: op.structId,
                beforeBBox: bboxArrayToObject(op.before),
                afterBBox: bboxArrayToObject(op.after),
            };
        case "create-element":
            // Maps to the existing `add-child` server op (the C# side
            // already has `AddChildOp` with an optional BBox — see
            // `backend/src/AdaTool.Cli/Pdfs/Model/EditOp.cs`).
            // The SPA-side editor allocates the structId locally so it
            // can render the new card immediately; the server reassigns
            // a path-form id on commit and reports it via
            // ##EDITOR_EDIT## (see EditOpResult.AssignedStructId).
            return {
                kind: "add-child",
                parentId: op.parentStructId ?? STRUCT_TREE_ROOT_ID,
                role: String(op.role ?? "P"),
                insertIndex: typeof op.insertIndex === "number" ? op.insertIndex : null,
                bBox: bboxArrayToObject(op.bbox),
            };
        case "delete-element":
            // Maps to the existing `remove-child` server op (the C# side
            // has `RemoveChildOp` routing through
            // `PdfStructTreeKidsMutator.RemoveChild`). The server resolves
            // the parent from the StructId path (see ApplyRemoveChild in
            // `EditorEditRunner.cs`), so we don't carry parentId on the
            // wire — the path is sufficient.
            //
            // RemoveChild throws on elems with MCID descendants. The
            // dispatcher surfaces that error verbatim (see
            // EditOpDispatchTests.Dispatch_RemoveChild_OnElemWithMcids_*),
            // and the SPA's per-op result handling will render an
            // actionable toast.
            return {
                kind: "remove-child",
                structId: op.structId,
            };
        case "set-title":
        case "set-attestation":
            // SPA-only; no struct-elem mutation. The values live in
            // /Info or session metadata, not in the per-elem dispatch.
            return null;
        case "reassociate-mcids":
            // Pass-through: `mcid-reassociate.js#buildReassociateOp` already
            // emits the wire shape (kind="reassociate-mcids", reassocKind,
            // mcids[], targetStructId, bbox). Strip undo-only fields.
            // See `backend/src/AdaTool.Cli/Pdfs/Model/EditOp.cs#ReassociateMcidsOp`.
            return {
                kind: "reassociate-mcids",
                reassocKind: op.reassocKind,
                mcids: Array.isArray(op.mcids) ? op.mcids : [],
                targetStructId: op.targetStructId ?? null,
                bbox: op.bbox ?? null,
            };
        case "clear-all-tags":
            // Whole-document strip: the wire op carries only the discriminator.
            // The server (EditorEditRunner.ApplyClearAllTags) removes
            // /StructTreeRoot + clears /MarkInfo/Marked so the download is
            // untagged — see backend/src/AdaTool.PdfModel/Model/EditOp.cs#ClearAllTagsOp.
            return { kind: "clear-all-tags" };
        default:
            return null;
    }
}
// Translate a list of SPA ops to wire ops, dropping non-mappable ops.
//
// Discards `create-element` ops whose role is UNDEFINED (the sentinel for
// "user inserted a new element but hasn't yet picked a type"). The
// companion behaviour — blocking Save while UNDEFINED elements exist —
// lives in `performSave`'s pendingInsertStore check; this filter is a
// defensive second line so a stale UNDEFINED can never reach the server
// even if the block is bypassed (e.g. by a stale pendingInsertStore size
// after a partial cleanup).
//
// For any UNDEFINED `create-element` we drop, we also drop subsequent ops
// (set-role, set-bbox, etc.) targeting the same locally-allocated
// structId — without the parent create, those mutations have nowhere to
// land on the server.
//
// Format dispatch (Phase 6a): the PDF path runs the PDF `opToWireOp`
// discriminated-union translation. The OOXML formats (pptx/docx/xlsx) POST a
// DIFFERENT wire vocabulary (the C# `OoxmlEditOp` union — `shapeId`/`beforeAlt`
// keys, first-class `mark-decorative`), so the o365 boot pre-translates each
// ViewOp to its OOXML wire shape (`viewOpToOoxmlWireOp`) and stores THAT in the
// opLog. For pptx/docx/xlsx we therefore pass the already-wire-shaped entries through
// (only requiring a string `kind`) rather than routing them through the PDF
// `opToWireOp` — keeping save-flow the single shared save machinery (rule #11)
// without importing the OOXML view types.
export function aggregateOps(spaOps, format) {
    if (format === "pptx" || format === "docx" || format === "xlsx") {
        return (spaOps ?? [])
            .filter((op) => !!op && typeof op.kind === "string")
            .map((op) => op);
    }
    const out = [];
    const droppedStructIds = new Set();
    for (const op of spaOps ?? []) {
        if (op?.kind === "create-element"
            && typeof op.role === "string"
            && op.role.toUpperCase() === ROLE_UNDEFINED) {
            // Track the locally-allocated structId so downstream ops
            // (set-role, set-bbox, set-alt-text targeting this elem)
            // are also dropped — they'd fail server-side without the
            // parent create.
            if (op.structId)
                droppedStructIds.add(op.structId);
            continue;
        }
        if (op?.structId && droppedStructIds.has(op.structId)) {
            continue;
        }
        const wire = opToWireOp(op);
        if (wire !== null)
            out.push(wire);
    }
    return out;
}
// Snapshot the op log into a list of SPA ops in applied order. Caller
// is responsible for translating via aggregateOps.
export function snapshotOpLog(opLog) {
    if (!opLog || typeof opLog.snapshot !== "function")
        return [];
    const snap = opLog.snapshot();
    // Only forward-applied ops (entries up to cursor) are eligible —
    // anything beyond cursor is a redo-able tail of undone ops.
    const cursor = typeof snap.cursor === "number" ? snap.cursor : (snap.entries ?? []).length;
    return (snap.entries ?? []).slice(0, cursor);
}
// ─── Per-format endpoint resolution ──────────────────────────────────────────
/**
 * Resolve the POST URL for the per-format editor-edit endpoint.
 *
 * - `pdf`  → `/api/editor-edit/<jobId>` (legacy PDF path)
 * - `pptx` → `/api/editor-edit-pptx/<jobId>` (Phase 3)
 * - `docx` → `/api/editor-edit-docx/<jobId>` (Phase 3)
 * - `xlsx` → `/api/editor-edit-xlsx/<jobId>` (xlsx-a11y-editor Phase 4)
 */
export function resolveEditEndpoint(jobId, format, baseUrl) {
    const encoded = encodeURIComponent(jobId);
    const fmt = format ?? "pdf";
    if (fmt === "pptx") {
        return appendTokenIfPresent(`${baseUrl}/api/editor-edit-pptx/${encoded}`);
    }
    if (fmt === "docx") {
        return appendTokenIfPresent(`${baseUrl}/api/editor-edit-docx/${encoded}`);
    }
    if (fmt === "xlsx") {
        return appendTokenIfPresent(`${baseUrl}/api/editor-edit-xlsx/${encoded}`);
    }
    // Default: PDF
    return appendTokenIfPresent(`${baseUrl}/api/editor-edit/${encoded}`);
}
// POST the wire envelope to the per-format edit endpoint.
export async function postEditorEdit(jobId, wireOps, opts = {}) {
    if (!jobId)
        return { ok: false, error: "missing jobId" };
    const fetchImpl = opts.fetchImpl ?? (typeof fetch !== "undefined" ? fetch : null);
    if (!fetchImpl)
        return { ok: false, error: "fetch is not available" };
    const base = opts.baseUrl ?? "";
    const url = resolveEditEndpoint(jobId, opts.format, base);
    let resp;
    try {
        resp = await fetchImpl(url, {
            method: "POST",
            headers: { "Content-Type": "application/json", "Accept": "application/json" },
            body: JSON.stringify({ ops: wireOps }),
            signal: opts.signal ?? null,
        });
    }
    catch (err) {
        return { ok: false, error: `network error: ${err instanceof Error ? err.message : String(err)}` };
    }
    let body = null;
    try {
        body = await resp.json();
    }
    catch {
        body = null;
    }
    if (!resp.ok) {
        const detail = (body && (body["detail"] ?? body["error"])) ?? resp.statusText;
        // eslint-disable-next-line @typescript-eslint/no-base-to-string -- detail is unknown-typed wire IO; String() is the explicit conversion
        return { ok: false, status: resp.status, error: `HTTP ${resp.status}: ${String(detail)}` };
    }
    return {
        ok: !!(body?.["ok"]),
        status: resp.status,
        // eslint-disable-next-line @typescript-eslint/no-base-to-string -- body values are unknown-typed wire IO; String() is the explicit conversion
        error: body?.["error"] ? String(body["error"]) : undefined,
        results: body && Array.isArray(body["results"]) ? body["results"] : [],
        // eslint-disable-next-line @typescript-eslint/no-base-to-string -- body values are unknown-typed wire IO; String() is the explicit conversion
        savedAt: body?.["savedAt"] ? String(body["savedAt"]) : undefined,
    };
}
// High-level save coordinator. Aggregates the oplog, POSTs, returns a
// structured result the editor can use to render a toast and clear the
// log on success.
//
// Save-block invariant: refuse if any UNDEFINED elements remain in the
// pending-insert store. The caller still owns blocking the click before
// we even get here (see editor.js's capture-phase listener) — this is
// a defensive second line.
export async function performSave(ctx) {
    if (!ctx || typeof ctx !== "object") {
        return { ok: false, error: "performSave: missing context" };
    }
    if (!ctx.jobId) {
        return { ok: false, error: "performSave: no jobId — open the editor via a job link" };
    }
    if (ctx.pendingInsertStore && typeof ctx.pendingInsertStore.size === "function"
        && ctx.pendingInsertStore.size() > 0) {
        return {
            ok: false,
            blocked: true,
            error: "Pending insertions must be assigned a type before saving.",
        };
    }
    // FOLD-1 exactly-once floor: POST only the un-applied tail. Entries already
    // flushed to the checkpoint (this same opLog epoch) are dropped so a re-POST
    // cannot double-apply them server-side (H4′). A fresh OpLog (explicit-save
    // reset) is a new epoch → nothing flushed yet → full snapshot.
    const fullSnapshot = snapshotOpLog(ctx.opLog);
    const start = ctx.opLog === _flushedOpLog ? _flushedEntryCount : 0;
    const spaOps = fullSnapshot.slice(start);
    if (fullSnapshot.length > 0 && spaOps.length === 0) {
        // The ops in this epoch were ALL already durably flushed — a no-op
        // success (avoids the explicit-save double-POST + auto-save cumulative
        // re-POST). A genuinely-empty snapshot falls through to the POST so an
        // empty-payload save still round-trips (error passthrough).
        return { ok: true, opCount: 0 };
    }
    const wireOps = aggregateOps(spaOps, ctx.format);
    const result = await postEditorEdit(ctx.jobId, wireOps, {
        baseUrl: ctx.baseUrl,
        signal: ctx.signal,
        fetchImpl: ctx.fetchImpl,
        format: ctx.format,
    });
    if (result.ok) {
        // Advance the high-water past the entries this POST flushed. Only on
        // success — a failed POST retains the tail for the next cycle (ESE-7).
        _flushedOpLog = ctx.opLog;
        _flushedEntryCount = fullSnapshot.length;
    }
    return { ...result, opCount: wireOps.length };
}
// ─── Auto-save cadence (Phase 4) ─────────────────────────────────────────────
/**
 * Notify save-flow that one op was applied to the oplog.
 *
 * If `opsSinceFlush` has reached the threshold AND there is no flush
 * already in flight (ESE-8), triggers an auto-save flush using the
 * provided context.
 *
 * The caller is responsible for passing a ctx whose `opLog.snapshot()`
 * reflects the current un-flushed tail at the time of the call.
 *
 * Returns the flush result if a flush was triggered, or `null` if no
 * flush was needed.
 */
export async function recordOpApplied(ctx) {
    autoSaveState.opsSinceFlush += 1;
    if (autoSaveState.opsSinceFlush < _autoSaveOpThreshold) {
        return null;
    }
    if (_inFlightFlushPromise !== null) {
        // A flush is already in flight; the threshold counter keeps
        // accumulating — the next flush will catch the residual tail.
        return null;
    }
    // background=true: this is the auto-save cadence, so surface the
    // NON-BLOCKING "Autosaving…" indicator on the Save-to-Cloud button.
    return _doFlush(ctx, /* background */ true);
}
/**
 * Force-flush the un-flushed tail before a download.
 *
 * ESE-8: awaits any in-flight auto-save flush first, then flushes the
 * residual tail (if any) regardless of threshold. This ensures the
 * server has the latest edits before the download is served.
 *
 * No-ops when the oplog is empty / no pending ops.
 */
export async function flushBeforeDownload(ctx) {
    // ESE-8: await any in-flight flush first so we don't double-POST.
    if (_inFlightFlushPromise !== null) {
        await _inFlightFlushPromise;
        // After the in-flight flush resolves, check for residual ops.
        // The auto-save may have consumed everything — only flush if the
        // tail is non-empty.
    }
    if (autoSaveState.opsSinceFlush === 0) {
        return null;
    }
    return _doFlush(ctx);
}
/**
 * Internal: perform one flush cycle, setting/clearing the in-flight promise.
 *
 * - Sets `autoSaveState.pending = true` + arms `_inFlightFlushPromise`.
 * - On SUCCESS: resets `opsSinceFlush` (ESE-7) and bumps `flushedSeq`.
 * - On FAILURE: leaves `opsSinceFlush` intact so the tail is retried next
 *   cycle (ESE-7: counter resets only on success).
 * - Always clears `pending` + `_inFlightFlushPromise` in the finally block.
 */
function _doFlush(ctx, background = false) {
    const opsBeforeFlush = autoSaveState.opsSinceFlush;
    const seqNum = autoSaveState.flushedSeq + 1;
    autoSaveState.pending = true;
    autoSaveState.lastFlushTs = Date.now();
    // Only background (auto-save) flushes drive the "Autosaving…" indicator.
    // An explicit Save-to-Cloud already owns the button's SAVING state, so
    // firing the observer there would fight with it.
    if (background)
        _notifyAutoSaveState(true);
    const promise = (async () => {
        let result;
        try {
            result = await performSave(ctx);
        }
        finally {
            autoSaveState.pending = false;
            _inFlightFlushPromise = null;
            if (background)
                _notifyAutoSaveState(false);
        }
        if (result.ok) {
            // ESE-7: reset counter ONLY on success.
            autoSaveState.opsSinceFlush = 0;
            autoSaveState.flushedSeq = seqNum;
            log.info("[save-flow] auto-save flush ok — ops=" + opsBeforeFlush + " seq=" + seqNum);
        }
        else {
            log.warn("[save-flow] auto-save flush failed (tail retained) — ops=" + opsBeforeFlush + " seq=" + seqNum + " error=" + (result.error ?? "(none)"));
        }
        return result;
    })();
    _inFlightFlushPromise = promise;
    return promise;
}
